/**
 * Template EFFECTIF de l'espace actif (F1b, #115) — la source UNIQUE dont le board et
 * l'affichage des items dérivent leurs colonnes (workflow) et leurs types.
 *
 * Contrat de résolution (identique au worker) :
 *   • `workspace.template_id` non nul → le `config` de ce template (config_templates) ;
 *   • sinon (grande majorité), ou en chargement / erreur / template vide → le MODÈLE
 *     INTÉGRÉ (lib/workitemModel.ts), miroir exact du « Default ».
 *
 * Règle d'or : un espace SANS template doit s'afficher EXACTEMENT comme avant. Le repli
 * reproduit donc à l'identique les 4 statuts (todo/in_progress/in_review/done + libellés)
 * et les types actuels. Le retour est TOUJOURS défini et jamais vide : si le template chargé
 * n'a pas de workflow (ou pas de types), on retombe sur le défaut plutôt que sur du vide.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api, type TemplateItemType, type TemplateStatusDef } from '@/api/client'
import { ITEM_TYPES, WORKFLOW } from '@/lib/workitemModel'
import { etapesEffectives, type EtapeCycleDeVie } from '@/lib/lifecycleTemplate'
import { useWorkspace } from '@/hooks/useWorkspace'

export interface EffectiveTemplate {
  /** Statuts du board, dans l'ordre : chaque colonne = { status, label }. Jamais vide. */
  workflow: TemplateStatusDef[]
  /** Types de work-item (label par kind, lignée). Jamais vide. */
  itemTypes: TemplateItemType[]
  /** `true` UNIQUEMENT quand `workflow` provient du template chargé (pas du repli intégré) :
   *  permet aux badges de statut d'afficher les libellés du template (ex. « Bloqué ») sans
   *  casser l'i18n canonique des espaces sans template. */
  workflowFromTemplate: boolean
  /**
   * `true` UNIQUEMENT quand `itemTypes` provient réellement du template chargé de l'espace,
   * et non du repli intégré. Permet aux consommateurs (cartes du board) d'utiliser les LIBELLÉS
   * du template (ex. « Epic » renommé « Capacité ») sans casser l'i18n des espaces sans template :
   * en chargement / erreur / template vide, le drapeau reste `false` → repli sur `kindLabel(kind, t)`
   * (i18n), jamais sur le label FR brut du modèle intégré.
   */
  itemTypesFromTemplate: boolean
  /**
   * Le NOM du template rattaché à l'espace, une fois chargé (ex. « Assurance (démo) ») ; `null`
   * quand l'espace n'a pas de template (utilise le Default intégré), ou pendant le chargement.
   * Sert à RENDRE VISIBLE le modèle utilisé par un projet (#124) — jusqu'ici invisible dans l'UI.
   */
  templateName: string | null
  /**
   * L'IDENTIFIANT du template rattaché à l'espace actif, ou `null`. C'est lui qui permet à
   * l'écran de configuration d'ouvrir sur le template RÉELLEMENT utilisé par le projet courant,
   * au lieu du Default ou du premier de la liste — et donc d'avertir quand on en édite un autre.
   */
  templateId: string | null
  /** Étapes du cycle de vie, déclarées par le template ou repli intégré. Jamais vide. */
  lifecycle: EtapeCycleDeVie[]
  /** `true` UNIQUEMENT quand `lifecycle` provient du template chargé (pas du repli intégré). */
  lifecycleFromTemplate: boolean
}

// Repli = le modèle canonique codé en dur, projeté dans la forme des templates. Calculé une
// seule fois (constantes de module) : c'est le comportement d'AVANT pour les espaces sans template.
const FALLBACK_WORKFLOW: TemplateStatusDef[] = WORKFLOW.map((s) => ({
  status: s.status,
  label: s.label,
  to: [...s.to],
}))

// Les clés optionnelles ne sont posées que si définies (exactOptionalPropertyTypes) : on
// n'écrit jamais `key: undefined`, ce qui reproduit fidèlement le modèle intégré.
const FALLBACK_ITEM_TYPES: TemplateItemType[] = ITEM_TYPES.map((it) => ({
  kind: it.kind,
  label: it.label,
  description: it.description,
  ...(it.richBody !== undefined ? { richBody: it.richBody } : {}),
  generates: [...it.generates],
  fields: it.fields.map((f) => ({
    key: f.key,
    label: f.label,
    type: f.type,
    ...(f.required !== undefined ? { required: f.required } : {}),
    ...(f.asTitle !== undefined ? { asTitle: f.asTitle } : {}),
    ...(f.hint !== undefined ? { hint: f.hint } : {}),
  })),
}))

export function useEffectiveTemplate(): EffectiveTemplate {
  const { active } = useWorkspace()
  // `active` est null pour « Tous les projets », aucun espace ou chargement : templateId reste
  // null → repli défaut. La requête n'est lancée que si un template est réellement rattaché.
  const templateId = active?.template_id ?? null

  const templateQuery = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => api.getTemplate(templateId as string),
    enabled: Boolean(templateId),
    // La configuration change rarement : on évite un aller-retour à chaque montage du board.
    staleTime: 5 * 60 * 1000,
  })

  return useMemo<EffectiveTemplate>(() => {
    const config = templateQuery.data?.config
    // Un workflow/type ABSENT ou VIDE retombe sur le défaut : jamais de board sans colonnes,
    // jamais de types manquants — même si le template est incomplet ou en cours de chargement.
    const templateWorkflow = config?.workflow && config.workflow.length > 0
      ? config.workflow
      : null
    const workflow = templateWorkflow ?? FALLBACK_WORKFLOW
    // On garde la référence des types VENANT du template (ou null) pour, d'un même test, dériver
    // à la fois les `itemTypes` effectifs ET le drapeau `itemTypesFromTemplate` — sans assertion.
    const templateItemTypes = config?.itemTypes && config.itemTypes.length > 0
      ? config.itemTypes
      : null
    const itemTypes = templateItemTypes ?? FALLBACK_ITEM_TYPES
    // Le cycle de vie suit exactement la même règle : déclaré et non vide → il fait foi ;
    // absent, vide, en chargement ou en erreur → le cycle intégré, jamais du vide.
    const templateLifecycle = config?.lifecycle && config.lifecycle.length > 0
      ? config.lifecycle
      : null
    return {
      workflow, itemTypes,
      itemTypesFromTemplate: templateItemTypes !== null,
      workflowFromTemplate: templateWorkflow !== null,
      templateName: templateQuery.data?.name ?? null,
      templateId,
      lifecycle: etapesEffectives(templateLifecycle ?? undefined),
      lifecycleFromTemplate: templateLifecycle !== null,
    }
  }, [templateQuery.data, templateId])
}
